import axios from 'axios';
import { apiBaseUrl } from './apiBase';
import { isApiCoolingDown, noteApiFailure, noteApiSuccess } from './apiRateLimit';
import type { WeatherModelId } from '../store/types';

/**
 * أعلى حرارة سطح (°م) يُعدّ الهطول عندها ثلجاً. 1.0° لا 0°: الثلج يصل الأرض سليماً حتى
 * قرابة درجة فوق الصفر (الطبقة الباردة أعلى السطح). مصدر وحيد لهذه العتبة — تستعمله
 * تسمية التلميح على الخريطة (hoverValueFormat) وتصحيح أيقونة التوقّعات هنا معاً، فلا
 * تختلف الخريطة عن اللوحة في تصنيف الحالة نفسها.
 */
export const SNOW_TEMP_MAX_C = 1.0;

export interface WeatherResponse {
    latitude: number;
    longitude: number;
    timezone: string;
    timezone_abbreviation: string;
    elevation: number;
    stale?: boolean;
    providerMessage?: string;
    model?: WeatherModelId;
    hourly: {
        time: Array<string | number>;
        temperature_2m: number[];
        apparent_temperature: number[];
        dew_point_2m: number[];
        relative_humidity_2m: number[];
        wind_speed_10m: number[];
        wind_direction_10m: number[];
        wind_gusts_10m: number[];
        precipitation: number[];
        rain: number[];
        snowfall: number[];
        weather_code: number[];
        cloud_cover: number[];
        surface_pressure: number[];
        visibility: number[];
    };
    current?: {
        time: string | number;
        temperature_2m: number;
        wind_speed_10m: number;
        weather_code: number;
    };
}

class WeatherService {
    private baseUrl = apiBaseUrl;
    private cache = new Map<string, { data: WeatherResponse; timestamp: number }>();
    private inFlight = new Map<string, Promise<WeatherResponse>>();
    private readonly cacheTtl = 15 * 60 * 1000;

    private normalizeLongitude(value: number): number {
        return ((((value + 180) % 360) + 360) % 360) - 180;
    }

    private buildCacheKey(lat: number, lon: number, model: WeatherModelId, hours: number): string {
        return [lat.toFixed(3), lon.toFixed(3), model, hours].join('_');
    }

    private normalizeTimes(times: Array<string | number>): string[] {
        return times.map((time) => String(time));
    }

    async getAvailableModels(): Promise<any[]> {
        const response = await axios.get(`${this.baseUrl}/models`);
        return response.data.models;
    }

    async getWeatherData(
        lat: number,
        lon: number,
        model: WeatherModelId = 'GFS',
        hours: number = 168
    ): Promise<WeatherResponse> {
        const normalizedLon = this.normalizeLongitude(lon);
        const cacheKey = this.buildCacheKey(lat, normalizedLon, model, hours);
        const cached = this.cache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < this.cacheTtl) {
            return cached.data;
        }

        const existing = this.inFlight.get(cacheKey);
        if (existing) return existing;

        // قاطع الدائرة: أثناء التهدئة لا نُرسل طلباً جديداً للمزوّد.
        if (isApiCoolingDown()) {
            if (cached) return cached.data;
            return Promise.reject(new Error('Weather API temporarily unavailable (rate limited).'));
        }

        // توقّعات النقطة من مكعّبات الـ rasters المحلّية (بلا Open-Meteo): مجاني، سريع، بلا 429.
        // نفس بنية WeatherResponse، وبنفس زمن دورة النموذج (run_epoch) فيتطابق مع شريط الوقت.
        const request = axios.get(`${this.baseUrl}/point`, {
            params: { latitude: lat, longitude: normalizedLon, model, hours },
            timeout: 30000,
        })
            .then((response) => {
                const data = response.data as WeatherResponse;
                noteApiSuccess();
                this.cache.set(cacheKey, { data, timestamp: Date.now() });
                return data;
            })
            .catch((error) => {
                noteApiFailure(error);
                if (cached) return cached.data;
                throw new Error(error.response?.data?.message ?? 'Unable to fetch live weather data from the API.');
            })
            .finally(() => {
                this.inFlight.delete(cacheKey);
            });

        this.inFlight.set(cacheKey, request);
        return request;
    }

    async getWindData(lat: number, lon: number, model: WeatherModelId = 'GFS', hours = 48) {
        const data = await this.getWeatherData(lat, lon, model, hours);
        return {
            time: this.normalizeTimes(data.hourly.time),
            windSpeed: data.hourly.wind_speed_10m,
            windDirection: data.hourly.wind_direction_10m,
            windGusts: data.hourly.wind_gusts_10m,
        };
    }

    async getPrecipitationData(lat: number, lon: number, model: WeatherModelId = 'GFS', hours = 168) {
        const data = await this.getWeatherData(lat, lon, model, hours);
        return {
            time: this.normalizeTimes(data.hourly.time),
            precipitation: data.hourly.precipitation,
            rain: data.hourly.rain,
            snowfall: data.hourly.snowfall,
        };
    }

    async getTemperatureData(lat: number, lon: number, model: WeatherModelId = 'GFS', hours = 168) {
        const data = await this.getWeatherData(lat, lon, model, hours);
        return {
            time: this.normalizeTimes(data.hourly.time),
            temperature: data.hourly.temperature_2m,
            apparentTemperature: data.hourly.apparent_temperature,
            dewPoint: data.hourly.dew_point_2m,
        };
    }

    async getPressureData(lat: number, lon: number, model: WeatherModelId = 'GFS', hours = 168) {
        const data = await this.getWeatherData(lat, lon, model, hours);
        return { time: this.normalizeTimes(data.hourly.time), pressure: data.hourly.surface_pressure };
    }

    async getHumidityData(lat: number, lon: number, model: WeatherModelId = 'GFS', hours = 168) {
        const data = await this.getWeatherData(lat, lon, model, hours);
        return {
            time: this.normalizeTimes(data.hourly.time),
            humidity: data.hourly.relative_humidity_2m,
            dewPoint: data.hourly.dew_point_2m,
        };
    }

    async getCloudData(lat: number, lon: number, model: WeatherModelId = 'GFS', hours = 168) {
        const data = await this.getWeatherData(lat, lon, model, hours);
        return { time: this.normalizeTimes(data.hourly.time), cloudCover: data.hourly.cloud_cover };
    }

    /**
     * Determine whether a given moment is "day" or "night".
     * Falls back to a simple 6:00-18:00 local-hour heuristic when no
     * sunrise/sunset data is available.
     */
    isDaytime(date: Date = new Date()): boolean {
        const hour = date.getHours();
        return hour >= 6 && hour < 18;
    }

    decodeWeatherCode(
        code: number,
        isDay: boolean = true
    ): { condition: string; icon: string; description: string } {
        const codes: Record<number, { condition: string; icon: string; description: string }> = {
            0: {
                condition: 'Clear sky',
                icon: isDay ? 'clear-day' : 'clear-night',
                description: 'Clear sky',
            },
            1: {
                condition: 'Mainly clear',
                icon: isDay ? 'partly-cloudy-day' : 'partly-cloudy-night',
                description: 'Mainly clear',
            },
            2: {
                condition: 'Partly cloudy',
                icon: isDay ? 'partly-cloudy-day-alt' : 'partly-cloudy-night-alt',
                description: 'Partly cloudy',
            },
            3: { condition: 'Overcast', icon: 'overcast', description: 'Overcast' },
            45: { condition: 'Fog', icon: 'overcast-alt', description: 'Fog' },
            48: { condition: 'Rime fog', icon: 'overcast-alt', description: 'Depositing rime fog' },
            51: {
                condition: 'Light drizzle',
                icon: isDay ? 'rain-day' : 'rain-night',
                description: 'Light drizzle',
            },
            53: {
                condition: 'Moderate drizzle',
                icon: isDay ? 'rain-day' : 'rain-night',
                description: 'Moderate drizzle',
            },
            55: { condition: 'Dense drizzle', icon: 'rain-heavy', description: 'Dense drizzle' },
            61: {
                condition: 'Light rain',
                icon: isDay ? 'rain-day' : 'rain-night',
                description: 'Slight rain',
            },
            63: { condition: 'Moderate rain', icon: 'rain-generic', description: 'Moderate rain' },
            65: { condition: 'Heavy rain', icon: 'rain-heavy', description: 'Heavy rain' },
            // كل ما يتساقط ثلجاً يأخذ أيقونة الغيمة ذات الندفات (icons-18): الندفة المجرّدة
            // (icons-19 / 'snow-generic') تصلح رمزاً للبرودة لا لحالة هطول، وكانت تظهر
            // للثلج الغزير بلا غيمة فتبدو دخيلة بين بقيّة أيقونات الحالات.
            56: { condition: 'Freezing drizzle', icon: 'snow', description: 'Light freezing drizzle' },
            57: { condition: 'Freezing drizzle', icon: 'snow', description: 'Dense freezing drizzle' },
            66: { condition: 'Freezing rain', icon: 'snow', description: 'Light freezing rain' },
            67: { condition: 'Freezing rain', icon: 'snow', description: 'Heavy freezing rain' },
            71: { condition: 'Light snow', icon: 'snow', description: 'Slight snow' },
            73: { condition: 'Moderate snow', icon: 'snow', description: 'Moderate snow' },
            75: { condition: 'Heavy snow', icon: 'snow', description: 'Heavy snow' },
            77: { condition: 'Snow grains', icon: 'snow', description: 'Snow grains' },
            85: { condition: 'Light snow showers', icon: 'snow', description: 'Slight snow showers' },
            86: { condition: 'Heavy snow showers', icon: 'snow', description: 'Heavy snow showers' },
            80: {
                condition: 'Light showers',
                icon: isDay ? 'rain-day' : 'rain-night',
                description: 'Slight rain showers',
            },
            81: { condition: 'Moderate showers', icon: 'rain-wind', description: 'Moderate rain showers' },
            // زخّة عنيفة ليست عاصفة رعدية: أيقونة العاصفة (بالصاعقة) كانت تَعِد ببرق لا يذكره
            // الرمز 82 أصلاً، فصارت أيقونة المطر الغزير.
            82: { condition: 'Heavy showers', icon: 'rain-heavy', description: 'Violent rain showers' },
            95: {
                condition: 'Thunderstorm',
                icon: isDay ? 'storm-day' : 'storm-night',
                description: 'Thunderstorm',
            },
            // العواصف الرعدية كلها بأيقونة الصاعقة مع المطر (icons-01/08 حسب الليل والنهار)،
            // فيبقى البرق ظاهراً في كل الحالات 95/96/99 لا في 95 وحدها.
            96: {
                condition: 'Thunderstorm + hail',
                icon: isDay ? 'storm-day' : 'storm-night',
                description: 'Thunderstorm with hail',
            },
            99: {
                condition: 'Thunderstorm + hail',
                icon: isDay ? 'storm-day' : 'storm-night',
                description: 'Thunderstorm with heavy hail',
            },
        };

        return codes[code] || { condition: 'Unknown', icon: 'overcast', description: 'Unknown condition' };
    }

    /**
     * حالة الطقس مع تصحيح نوع الهطول بالحرارة/الثلج الفعليّين.
     *
     * السبب: رمز WMO القادم من النموذج يصف أحياناً هطولاً «مطراً» (61/63/80…) عند حرارة
     * تحت الصفر، فتظهر في اللوحة أيقونة مطر ونصّ «مطر خفيف» فوق نقطة حرارتها ‑14°.
     * فإن كان هناك ثلج فعليّ (snowfall > 0) أو كانت الحرارة ≤ 1°، نُبدل رمز المطر برمز
     * الثلج المكافئ في الشدّة (خفيف↔71، متوسط↔73، غزير↔75) قبل فكّ الترميز — فيتبدّل
     * الاسم والأيقونة معاً بلا مسار ثانٍ للأيقونات.
     *
     * العواصف الرعدية (95/96/99) لا تُبدَّل: أيقونة العاصفة صحيحة برداً كانت أو مطراً.
     */
    decodeWeatherCodeAdjusted(
        code: number,
        isDay: boolean = true,
        temperatureC?: number,
        snowfall?: number,
    ): { condition: string; icon: string; description: string } {
        return this.decodeWeatherCode(this.snowAdjustedCode(code, temperatureC, snowfall), isDay);
    }

    /** يحوّل رموز المطر/الرذاذ إلى رمز الثلج المكافئ عند ظروف التجمّد. */
    private snowAdjustedCode(code: number, temperatureC?: number, snowfall?: number): number {
        const freezing = (typeof temperatureC === 'number' && Number.isFinite(temperatureC) && temperatureC <= SNOW_TEMP_MAX_C)
            || (typeof snowfall === 'number' && snowfall > 0);
        if (!freezing) return code;
        const RAIN_TO_SNOW: Record<number, number> = {
            51: 71, 53: 73, 55: 75,   // رذاذ خفيف/متوسط/كثيف
            61: 71, 63: 73, 65: 75,   // مطر خفيف/متوسط/غزير
            80: 85, 81: 85, 82: 86,   // زخّات مطر → زخّات ثلج
        };
        return RAIN_TO_SNOW[code] ?? code;
    }

    convertTemperature(temp: number, toFahrenheit: boolean): number {
        return toFahrenheit ? (temp * 9 / 5) + 32 : temp;
    }

    convertWindSpeed(speed: number, unit: 'kmh' | 'mph' | 'ms' | 'knots'): number {
        switch (unit) {
            case 'mph': return speed * 0.621371;
            case 'knots': return speed * 0.539957;
            case 'ms': return speed / 3.6;
            default: return speed;
        }
    }

    convertPressure(pressure: number, toInHg: boolean): number {
        return toInHg ? pressure / 33.8639 : pressure;
    }

    convertPrecipitation(precip: number, toInch: boolean): number {
        return toInch ? precip / 25.4 : precip;
    }
}

export const weatherService = new WeatherService();
